import React, { useState } from 'react';
import { useTeamsList } from '@/modules/teams/list/hooks/useTeamsList';
import { useAuth } from '@/core/auth/AuthContext';
import { Users, CheckCircle2, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/utils/cn';

interface ProjectTeamInfoProps {
  form: any;
  updateForm: (patch: any) => void;
}

export function ProjectTeamInfo({ form, updateForm }: ProjectTeamInfoProps) {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  
  const { teams, isLoading } = useTeamsList(companyId);
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleTeam = (teamId: string) => {
    const currentTeams = form.teamIds || [];
    if (currentTeams.includes(teamId)) {
      updateForm({ teamIds: currentTeams.filter((id: string) => id !== teamId) });
    } else {
      updateForm({ teamIds: [...currentTeams, teamId] });
    }
  };

  return (
    <div className="bg-white rounded-md border border-slate-100 overflow-hidden">
      <div 
        className="p-4 border-b border-slate-50 bg-slate-50/30 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-blue-50 rounded-lg text-blue-600">
            <Users className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-sm font-normal text-slate-800 tracking-wider">Đội nhóm tham gia</h3>
            <p className="sm:text-sm text-xs text-slate-400 font-normal">Chọn các tổ đội thi công/bảo dưỡng cho dự án</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-xs font-normal text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md">
            Đã chọn: {form.teamIds?.length || 0}
          </span>
          <div className="text-slate-400">
            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </div>
      </div>

      {isExpanded && (

      <div className="p-4">
        {isLoading ? (
          <div className="py-8 flex flex-col items-center justify-center gap-2">
            <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-xs text-slate-400">Đang tải danh sách tổ đội...</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {teams.map((team) => {
              const isSelected = form.teamIds?.includes(team.id);
              return (
                <div
                  key={team.id}
                  onClick={() => toggleTeam(team.id)}
                  className={cn(
                    "flex items-center justify-between p-3 rounded-lg border transition-all cursor-pointer group",
                    isSelected 
                      ? "border-blue-200 bg-blue-50/50 " 
                      : "border-slate-100 hover:border-slate-200 hover:bg-slate-50"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center text-xs font-normal",
                      isSelected ? "bg-blue-100 text-blue-600" : "bg-slate-100 text-slate-500"
                    )}>
                      {team.name?.[0] || 'T'}
                    </div>
                    <div>
                      <p className="sm:text-sm text-xs font-normal text-slate-700 leading-tight">{team.name}</p>
                      <p className="sm:text-sm text-xs text-slate-400 ">{team.code || 'N/A'} • {team.team_type}</p>
                    </div>
                  </div>
                  {isSelected ? (
                    <CheckCircle2 className="w-4 h-4 text-blue-500" />
                  ) : (
                    <Plus className="w-4 h-4 text-slate-300 group-hover:text-slate-400" />
                  )}
                </div>
              );
            })}
            {teams.length === 0 && (
              <div className="col-span-2 py-8 text-center border-2 border-dashed border-slate-100 rounded-xl">
                <Users className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                <p className="text-xs text-slate-400">Chưa có dữ liệu tổ đội nào</p>
              </div>
            )}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
